/**
 * M5a data transparency: per-channel export and deletion of the stored YouTube data (Settings → Accounts), and the "Your data" card
 * (Privacy, revoking access at Google, export everything). Exports come from the API as an attachment and are saved as a file.
 */
import { useState } from "react";
import type { Account } from "../../api/types";
import { useToast } from "../../components/toast";
import { Button, Card, ConfirmDialog } from "../../components/ui";
import { useApi, useMutation } from "../../data/hooks";
import { saveBlob } from "../../lib/download";
import { dateTime, relative } from "../../lib/format";

export const GOOGLE_PERMISSIONS_URL = "https://myaccount.google.com/permissions";
export const DISCONNECT_DATA_NOTE = "This also revokes ContentOps' access with Google and deletes all stored YouTube data for this channel immediately.";

export function useExportYoutubeData() {
  const api = useApi();
  const toast = useToast();
  return useMutation((account: string, redact: "none" | "content" = "none") => api.exportYoutubeData({ account, redact }), {
    onSuccess: ({ blob, filename }) => { saveBlob(blob, filename); toast.ok(`Downloaded ${filename}.`); },
    onError: (e) => toast.error(e, "Couldn't export"),
  });
}

const TABLE_NAMES: [RegExp, string, string][] = [
  [/^yt_channels$/, "channel", "channels"], [/^yt_videos$/, "video", "videos"], [/^yt_playlists$/, "playlist", "playlists"],
  [/^yt_playlist_items$/, "playlist item", "playlist items"], [/^yt_analytics_/, "analytics row", "analytics rows"], [/^yt_sync_runs$/, "sync record", "sync records"],
  [/^account_profile$/, "channel profile", "channel profiles"], [/^publication_platform_status$/, "upload status check", "upload status checks"],
  [/^audit_channel_title$/, "logged channel name", "logged channel names"],
];
/** { yt_channels: 1, yt_videos: 24, yt_analytics_channel_daily: 95, … } → "1 channel, 24 videos, 95 analytics rows". */
export function describeDeleted(deleted: Record<string, number>): string {
  const groups = new Map<string, { one: string; many: string; n: number }>();
  for (const [table, n] of Object.entries(deleted)) {
    const hit = TABLE_NAMES.find(([re]) => re.test(table));
    const key = hit ? hit[2] : table;
    const g = groups.get(key) ?? { one: hit ? hit[1] : table, many: hit ? hit[2] : table, n: 0 };
    g.n += n;
    groups.set(key, g);
  }
  const parts = [...groups.values()].filter((g) => g.n > 0).map((g) => `${g.n} ${g.n === 1 ? g.one : g.many}`);
  return parts.length ? parts.join(", ") : "nothing was stored";
}

/** Freshness lines + Export / Delete stored data for one YouTube account card. */
export function AccountYoutubeData({ a }: { a: Account }) {
  const api = useApi();
  const toast = useToast();
  const exporter = useExportYoutubeData();
  const [confirm, setConfirm] = useState(false);
  const del = useMutation(() => api.deleteYoutubeData(a.id), {
    invalidate: ["accounts", "analytics"],
    onSuccess: (r) => { setConfirm(false); toast.ok(`Deleted the stored YouTube data for ${a.name}: ${describeDeleted(r.deleted)}.`); },
    onError: (e) => toast.error(e, "Couldn't delete"),
  });
  if (a.platform !== "youtube") return null;
  const live = a.auth_status === "connected" || a.auth_status === "expiring";
  const hasData = live || !!a.youtube_data_last_synced_at;
  return (
    <>
      {(a.authorization_verified_at || a.youtube_data_last_synced_at) && (
        <p className="muted small acct-fresh">
          {a.authorization_verified_at && <span>Authorization verified <time dateTime={a.authorization_verified_at} title={dateTime(a.authorization_verified_at)}>{relative(a.authorization_verified_at)}</time></span>}
          {a.authorization_verified_at && a.youtube_data_last_synced_at && <span aria-hidden> · </span>}
          {a.youtube_data_last_synced_at && <span>YouTube data refreshed <time dateTime={a.youtube_data_last_synced_at} title={dateTime(a.youtube_data_last_synced_at)}>{relative(a.youtube_data_last_synced_at)}</time></span>}
        </p>
      )}
      {hasData && (
        <div className="pub-actions">
          <Button size="sm" variant="ghost" icon="download" busy={exporter.pending} onClick={() => void exporter.mutate(a.id)}>Export data</Button>
          <Button size="sm" variant="ghost" icon="trash" onClick={() => setConfirm(true)}>Delete stored data</Button>
        </div>
      )}
      {confirm && (
        <ConfirmDialog title={`Delete stored YouTube data for ${a.name}?`} confirmLabel="Delete stored data" busy={del.pending}
          impact="ContentOps deletes everything it has stored from YouTube for this channel now: channel details, video and playlist lists, analytics figures and the sync log. Nothing changes on YouTube, and the channel stays connected."
          onCancel={() => setConfirm(false)} onConfirm={() => void del.mutate()}>
          <p className="muted small">{live ? "The next daily refresh fetches fresh data again. To stop that, disconnect the channel." : "It stays deleted until you connect this channel again."}</p>
        </ConfirmDialog>
      )}
    </>
  );
}

/** Settings → "Your data". */
export function YourDataCard() {
  const exporter = useExportYoutubeData();
  const [which, setWhich] = useState<"none" | "content" | null>(null);
  const run = (redact: "none" | "content") => { setWhich(redact); void exporter.mutate("all", redact).finally(() => setWhich(null)); };
  return (
    <Card title="Your data" id="your-data">
      <p className="small">ContentOps stores only what it needs from YouTube, refreshes it daily and deletes it when you disconnect a channel. Data that can't be refreshed for 30 days is deleted automatically.</p>
      <ul className="list your-data-links">
        <li><a className="text-link" href="/privacy">Privacy Policy</a></li>
        <li><a className="text-link" href={GOOGLE_PERMISSIONS_URL} target="_blank" rel="noopener noreferrer">Revoke access with Google<span className="sr-only"> (opens in a new tab)</span></a></li>
      </ul>
      <div className="row">
        <Button variant="secondary" icon="download" busy={exporter.pending && which === "none"} disabled={exporter.pending} onClick={() => run("none")}>Export all</Button>
        <Button variant="ghost" busy={exporter.pending && which === "content"} disabled={exporter.pending} onClick={() => run("content")}>Export all, titles hidden</Button>
      </div>
      <p className="muted small">A JSON file of everything stored from YouTube for every channel. “Titles hidden” replaces video and playlist titles, for sharing.</p>
    </Card>
  );
}
